/**
 * Weather at the rave (plan.md step 65) — the CONTRACT both sides read: the
 * API's `GET /editions/:id/weather`, the bundle's `weather` block (the offline
 * fallback), and the screens. Always the RAVE's place and clock, never the
 * phone's. Numbers are metric as stored; `units` says how a screen shows them
 * (`toDisplay*` below), decided from the festival's country.
 *
 * Two sources (§65.1): Google's forecast (`FORECAST`) once it reaches a day,
 * and NASA POWER's 20-year TYPICAL for days it does not reach yet — each day
 * says which it is, and a typical day is never shown as a forecast.
 */

import { dayBucket, dayLabel, wallClock, type DayBucket, type TimeZone } from './time.js';

export type WeatherSource = 'FORECAST' | 'TYPICAL';

/** A plain-words condition, from the provider's code; the screen picks the icon and the words. */
export const WEATHER_CONDITIONS = [
  'CLEAR', 'PARTLY_CLOUDY', 'CLOUDY', 'FOG', 'DRIZZLE', 'RAIN', 'HEAVY_RAIN', 'THUNDER', 'SNOW', 'WIND', 'UNKNOWN',
] as const;
export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number];

export interface WeatherNow {
  /** ISO instant the provider measured it. */
  readonly at: string;
  readonly tempC: number;
  readonly feelsC: number | null;
  /** 0–100. */
  readonly rainChance: number | null;
  readonly windKmh: number | null;
  readonly gustKmh: number | null;
  readonly uv: number | null;
  readonly condition: WeatherCondition;
}

export interface WeatherHour {
  /** ISO instant of the hour's start; shown in the rave's `timezone`. */
  readonly at: string;
  readonly tempC: number;
  readonly feelsC: number | null;
  readonly rainChance: number | null;
  readonly rainMm: number | null;
  readonly windKmh: number | null;
  readonly gustKmh: number | null;
  readonly condition: WeatherCondition;
}

export interface WeatherDay {
  /** The rave's calendar day, `YYYY-MM-DD` in its own zone. */
  readonly date: string;
  readonly source: WeatherSource;
  readonly highC: number | null;
  readonly lowC: number | null;
  readonly feelsHighC: number | null;
  /** Forecast: the provider's chance; typical: the share of the 20 years with ≥ 1 mm that day. */
  readonly rainChance: number | null;
  readonly rainMm: number | null;
  readonly windKmh: number | null;
  readonly gustKmh: number | null;
  readonly uv: number | null;
  readonly condition: WeatherCondition;
}

export type WeatherUnits = 'metric' | 'imperial';

export interface RaveWeather {
  readonly editionId: string;
  /** When the forecast was fetched (ISO), null when only typical days exist yet. */
  readonly fetchedAt: string | null;
  readonly timezone: string;
  readonly units: WeatherUnits;
  /** Present while a forecast exists for now (the rave is near or on). */
  readonly now: WeatherNow | null;
  /** The next 48 hours from `fetchedAt`, forecast only. */
  readonly hours: readonly WeatherHour[];
  /** One row per rave day (its day buckets), forecast where it reaches, typical otherwise. */
  readonly days: readonly WeatherDay[];
  /** "From 20 years at <place>": the years the typical rows are averaged over. */
  readonly typical: { readonly fromYear: number; readonly toYear: number } | null;
}

/** Countries that read °F and mph. */
const IMPERIAL = new Set(['US', 'LR', 'MM', 'BS', 'KY', 'PW', 'FM', 'MH']);

export function weatherUnitsFor(country: string | null | undefined): WeatherUnits {
  return country && IMPERIAL.has(country.toUpperCase()) ? 'imperial' : 'metric';
}

/** A temperature as the rave reads it, rounded: `°C` or `°F`. */
export function toDisplayTemp(c: number, units: WeatherUnits): number {
  return Math.round(units === 'imperial' ? c * 9 / 5 + 32 : c);
}

/** A wind speed as the rave reads it, rounded: km/h or mph. */
export function toDisplayWind(kmh: number, units: WeatherUnits): number {
  return Math.round(units === 'imperial' ? kmh / 1.609344 : kmh);
}

/* ------------------------------------------------------------------ the logic */
/*
 * Pure, as everything here: the API runs them when it stores and serves a
 * forecast, and a screen may run the same ones offline. `now` is always a
 * parameter.
 */

/** How bad a condition is, for "the worse of day and night" and nothing else. */
const SEVERITY: Record<WeatherCondition, number> = {
  UNKNOWN: 0, CLEAR: 1, PARTLY_CLOUDY: 2, CLOUDY: 3, FOG: 4, WIND: 5,
  DRIZZLE: 6, RAIN: 7, SNOW: 8, HEAVY_RAIN: 9, THUNDER: 10,
};

/** The worse of two conditions — a forecast day is its daytime AND its night. */
export function worseCondition(a: WeatherCondition, b: WeatherCondition): WeatherCondition {
  return SEVERITY[b] > SEVERITY[a] ? b : a;
}

/**
 * Google Weather API's `weatherCondition.type` → a plain-words condition.
 * Read by family, not by a table of every code, so a code Google adds later
 * lands in its family (or UNKNOWN) instead of breaking the page.
 */
export function googleCondition(type: string | null | undefined): WeatherCondition {
  const t = (type ?? '').toUpperCase();
  if (!t || t === 'TYPE_UNSPECIFIED') return 'UNKNOWN';
  if (t.includes('THUNDER')) return 'THUNDER';
  if (t.includes('SNOW') || t.includes('HAIL') || t.includes('SLEET')) return 'SNOW';
  if (t.includes('RAIN') || t.includes('SHOWER')) {
    if (t.includes('HEAVY')) return 'HEAVY_RAIN';
    if (t.startsWith('LIGHT_') || t.startsWith('CHANCE_')) return 'DRIZZLE';
    return 'RAIN';
  }
  if (t.includes('DRIZZLE')) return 'DRIZZLE';
  if (t.includes('FOG') || t.includes('HAZE') || t.includes('MIST')) return 'FOG';
  if (t.includes('WIND')) return 'WIND';
  if (t === 'PARTLY_CLOUDY') return 'PARTLY_CLOUDY';
  if (t.includes('CLOUD')) return 'CLOUDY';
  if (t.includes('CLEAR') || t.includes('SUNNY')) return 'CLEAR';
  return 'UNKNOWN';
}

const MAX_RAVE_DAYS = 31;
const DAY_MS = 86_400_000;

/**
 * The rave's days: every calendar day in ITS zone from the gates to the end.
 * An end exactly at midnight adds no empty day (the end is exclusive, as a
 * set ending at 22:00 is not playing at 22:00). No end: the gates' day.
 * At most 31 — weather is not a year planner.
 */
export function raveDays(gatesOpenAt: Date, endsAt: Date | null, timeZone: TimeZone): DayBucket[] {
  const first = dayBucket(gatesOpenAt, timeZone);
  const last = endsAt && endsAt > gatesOpenAt ? dayBucket(new Date(endsAt.getTime() - 1), timeZone) : first;
  const out: DayBucket[] = [];
  // Calendar arithmetic on the date itself, at UTC noon — no zone can move it.
  let at = Date.parse(`${first}T12:00:00Z`);
  const end = Date.parse(`${last}T12:00:00Z`);
  while (at <= end && out.length < MAX_RAVE_DAYS) {
    out.push(new Date(at).toISOString().slice(0, 10));
    at += DAY_MS;
  }
  return out;
}

/** The 20 full years before `now`'s — what "typical" is averaged over. */
export function typicalYears(now: Date): { fromYear: number; toYear: number } {
  const toYear = now.getUTCFullYear() - 1;
  return { fromYear: toYear - 19, toYear };
}

/** NASA POWER's daily point answer, `properties.parameter`: `YYYYMMDD` → value, -999 missing. */
export interface PowerDaily {
  readonly T2M_MAX?: Readonly<Record<string, number>>;
  readonly T2M_MIN?: Readonly<Record<string, number>>;
  readonly PRECTOTCORR?: Readonly<Record<string, number>>;
  /** m/s at 2 m. */
  readonly WS2M?: Readonly<Record<string, number>>;
}

/** A rainy day, for the typical "chance of rain": at least this much. */
export const TYPICAL_RAIN_MM = 1;

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * What is TYPICAL for each of the rave's calendar days at its place: over
 * `years`, the average high, low, rain (mm) and wind (WS2M, m/s → km/h), and
 * the share of years that day had ≥ 1 mm. A value nobody measured (-999, or
 * absent) is left out; a day with none at all is null, never 0. Typical rows
 * have no gusts, feels-like or UV — POWER does not give them. The condition is
 * only a hint from the rain chance (≥ 50 % RAIN, ≥ 25 % PARTLY_CLOUDY, else
 * CLEAR), and the row says TYPICAL so it is never shown as a forecast.
 */
export function typicalWeatherDays(
  param: PowerDaily, days: readonly DayBucket[], years: { fromYear: number; toYear: number },
): WeatherDay[] {
  const values = (series: Readonly<Record<string, number>> | undefined, mmdd: string): number[] => {
    if (!series) return [];
    const out: number[] = [];
    for (let y = years.fromYear; y <= years.toYear; y++) {
      const v = series[`${y}${mmdd}`];
      if (typeof v === 'number' && Number.isFinite(v) && v > -900) out.push(v);
    }
    return out;
  };
  const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
  return days.map((date): WeatherDay => {
    const mmdd = date.slice(5, 7) + date.slice(8, 10);
    const high = mean(values(param.T2M_MAX, mmdd));
    const low = mean(values(param.T2M_MIN, mmdd));
    const rain = values(param.PRECTOTCORR, mmdd);
    const wind = mean(values(param.WS2M, mmdd));
    const rainChance = rain.length ? Math.round((100 * rain.filter((r) => r >= TYPICAL_RAIN_MM).length) / rain.length) : null;
    const rainMm = mean(rain);
    return {
      date, source: 'TYPICAL',
      highC: high === null ? null : Math.round(high),
      lowC: low === null ? null : Math.round(low),
      feelsHighC: null,
      rainChance,
      rainMm: rainMm === null ? null : round1(rainMm),
      windKmh: wind === null ? null : Math.round(wind * 3.6),
      gustKmh: null, uv: null,
      condition: rainChance === null ? 'UNKNOWN' : rainChance >= 50 ? 'RAIN' : rainChance >= 25 ? 'PARTLY_CLOUDY' : 'CLEAR',
    };
  });
}

/**
 * One row per rave day, in order: the forecast's row for every day the
 * forecast ACTUALLY returned (so a provider reaching further moves the switch
 * by itself — no "10 days" anywhere), the typical row otherwise. A day neither
 * has is left out, never invented.
 */
export function mergeWeatherDays(
  forecast: readonly WeatherDay[], typical: readonly WeatherDay[], days: readonly DayBucket[],
): WeatherDay[] {
  const f = new Map(forecast.map((d) => [d.date, d]));
  const t = new Map(typical.map((d) => [d.date, d]));
  const out: WeatherDay[] = [];
  for (const date of days) {
    const row = f.get(date) ?? t.get(date);
    if (row) out.push(row);
  }
  return out;
}

/* ------------------------------------------------------------------ alerts */

/**
 * When the forecast is worth an announcement proposal (§65.5, the user,
 * 2026-10-10): feels-like ≥ 35 °C, gusts ≥ 60 km/h, a thunderstorm, and heavy
 * rain — ≥ 20 mm in a day or ≥ 7.5 mm in one hour (the usual "heavy rain"
 * line for an hour; 20 mm in a day soaks a campsite).
 */
export const WEATHER_ALERT = { heatFeelsC: 35, gustKmh: 60, rainDayMm: 20, rainHourMm: 7.5 } as const;

export type WeatherAlertKind = 'thunder' | 'wind' | 'rain' | 'heat';
const ALERT_ORDER: readonly WeatherAlertKind[] = ['thunder', 'wind', 'rain', 'heat'];

export interface WeatherAlertEvent {
  /** `<kind>:<date>` — stable across refreshes, so one event is one proposal. */
  readonly key: string;
  readonly kind: WeatherAlertKind;
  /** The rave day, `YYYY-MM-DD` in its zone. */
  readonly date: DayBucket;
  /** First hour and last hour + 1 (ISO) the hourly forecast shows it; null when only the day says so. */
  readonly from: string | null;
  readonly to: string | null;
  /** The worst of it: °C feels-like, km/h gust, mm rain; null for thunder. */
  readonly peak: number | null;
  /** A storm is URGENT (§65.5); the rest are ordinary notices. */
  readonly urgent: boolean;
}

/**
 * The events in a forecast that cross a line — FORECAST days only (a typical
 * row never warns anybody), and hours only on those days. One per kind per
 * day, ordered by day then thunder, wind, rain, heat.
 */
export function weatherAlerts(days: readonly WeatherDay[], hours: readonly WeatherHour[], timeZone: TimeZone): WeatherAlertEvent[] {
  const byDate = new Map<DayBucket, WeatherHour[]>();
  for (const h of hours) {
    const d = dayBucket(new Date(h.at), timeZone);
    byDate.set(d, [...(byDate.get(d) ?? []), h]);
  }
  const ge = (v: number | null | undefined, line: number) => typeof v === 'number' && v >= line;
  const max = (xs: (number | null | undefined)[]) => {
    const ns = xs.filter((x): x is number => typeof x === 'number');
    return ns.length ? Math.max(...ns) : null;
  };
  const out: WeatherAlertEvent[] = [];
  const forecastDays = days.filter((d) => d.source === 'FORECAST').sort((a, b) => a.date.localeCompare(b.date));
  for (const d of forecastDays) {
    const hs = [...(byDate.get(d.date) ?? [])].sort((a, b) => a.at.localeCompare(b.at));
    const hourlyRain = hs.reduce((s, h) => s + (h.rainMm ?? 0), 0);
    const checks: Record<WeatherAlertKind, { day: boolean; hit: (h: WeatherHour) => boolean; peak: () => number | null }> = {
      thunder: { day: d.condition === 'THUNDER', hit: (h) => h.condition === 'THUNDER', peak: () => null },
      wind: {
        day: ge(d.gustKmh, WEATHER_ALERT.gustKmh), hit: (h) => ge(h.gustKmh, WEATHER_ALERT.gustKmh),
        peak: () => max([d.gustKmh, ...hs.map((h) => h.gustKmh)]),
      },
      rain: {
        day: ge(d.rainMm, WEATHER_ALERT.rainDayMm), hit: (h) => ge(h.rainMm, WEATHER_ALERT.rainHourMm),
        peak: () => max([d.rainMm, hourlyRain || null]),
      },
      heat: {
        day: ge(d.feelsHighC, WEATHER_ALERT.heatFeelsC), hit: (h) => ge(h.feelsC, WEATHER_ALERT.heatFeelsC),
        peak: () => max([d.feelsHighC, ...hs.map((h) => h.feelsC)]),
      },
    };
    for (const kind of ALERT_ORDER) {
      const c = checks[kind];
      const hit = hs.filter(c.hit);
      if (!c.day && hit.length === 0) continue;
      const first = hit[0];
      const last = hit.at(-1);
      out.push({
        key: `${kind}:${d.date}`, kind, date: d.date,
        from: first ? new Date(first.at).toISOString() : null,
        to: last ? new Date(new Date(last.at).getTime() + 3_600_000).toISOString() : null,
        peak: c.peak(), urgent: kind === 'thunder',
      });
    }
  }
  return out;
}

/**
 * An alert in a raver's words, for the announcement proposed to the rave's
 * admins (they edit it before it goes out). The hours in the RAVE's clock and
 * the numbers in its units; no meteorology.
 */
export function weatherAlertText(e: WeatherAlertEvent, timeZone: TimeZone, units: WeatherUnits): { title: string; body: string } {
  const when = e.from && e.to
    ? `from ${wallClock(new Date(e.from), timeZone)} to ${wallClock(new Date(e.to), timeZone)}`
    : 'during the day';
  const day = dayLabel(e.date, timeZone);
  const temp = (c: number) => `${toDisplayTemp(c, units)} ${units === 'imperial' ? '°F' : '°C'}`;
  const wind = (k: number) => `${toDisplayWind(k, units)} ${units === 'imperial' ? 'mph' : 'km/h'}`;
  switch (e.kind) {
    case 'thunder':
      return {
        title: `Storm warning\n${day}`,
        body: `Thunderstorms are forecast ${when}. Secure your tent and anything loose, stay away from tall poles, trees and open fields, and follow the crew's directions.`,
      };
    case 'wind':
      return {
        title: `Strong wind\n${day}`,
        body: `Gusts${e.peak ? ` up to ${wind(e.peak)}` : ''} are forecast ${when}. Peg and tie down tents and gazebos, and pack away anything that can fly.`,
      };
    case 'rain':
      return {
        title: `Heavy rain\n${day}`,
        body: `${e.peak ? `About ${Math.round(e.peak)} mm of rain is` : 'Heavy rain is'} forecast ${when}. Bring a rain jacket and dry clothes, keep your tent's pegs firm, and watch your step on wet ground.`,
      };
    case 'heat':
      return {
        title: `Very hot\n${day}`,
        body: `It will feel like ${e.peak ? temp(e.peak) : temp(WEATHER_ALERT.heatFeelsC)} ${when}. Drink water often, find shade, wear a hat and sunscreen, and look out for each other.`,
      };
  }
}

/* ------------------------------------------------------------------ refresh */

const HOUR_MS = 3_600_000;
/** An hourly job runs a few seconds late or early; a refresh this close to due is due. */
const DUE_SLACK_MS = 10 * 60_000;

/**
 * How often a rave's forecast is fetched, by how far off its gates are (the
 * user, 2026-10-28): more than a month — once a week; a month to a week — once
 * a day; the last week — twice a day; from two days before the gates to the
 * end — every hour.
 */
export function weatherRefreshEveryMs(now: Date, gatesOpenAt: Date): number {
  const untilGates = gatesOpenAt.getTime() - now.getTime();
  if (untilGates > 30 * DAY_MS) return 7 * DAY_MS;
  if (untilGates > 7 * DAY_MS) return DAY_MS;
  if (untilGates > 2 * DAY_MS) return 12 * HOUR_MS;
  return HOUR_MS;
}

/**
 * Whether a rave's forecast should be fetched now (§65.1), on
 * `weatherRefreshEveryMs`'s rhythm, never after the end. Counted from the last
 * ATTEMPT, so a failing provider is not asked every minute.
 */
export function weatherRefreshDue(input: {
  now: Date; gatesOpenAt: Date; endsAt: Date | null; lastAttemptAt: Date | null;
}): boolean {
  const { now, gatesOpenAt, lastAttemptAt } = input;
  const end = input.endsAt ?? new Date(gatesOpenAt.getTime() + DAY_MS);
  if (now >= end) return false;
  const every = weatherRefreshEveryMs(now, gatesOpenAt);
  return !lastAttemptAt || now.getTime() - lastAttemptAt.getTime() >= every - DUE_SLACK_MS;
}

/**
 * A condition as one emoji — the pilot's own way of drawing weather, as the ☀️
 * sunset tag does. One table for the Weather page, Live and the always-on card.
 */
export function weatherIcon(c: WeatherCondition, night = false): string {
  switch (c) {
    case 'CLEAR': return night ? '🌙' : '☀️';
    case 'PARTLY_CLOUDY': return night ? '☁️' : '⛅';
    case 'CLOUDY': return '☁️';
    case 'FOG': return '🌫️';
    case 'DRIZZLE': return '🌦️';
    case 'RAIN': return '🌧️';
    case 'HEAVY_RAIN': return '🌧️';
    case 'THUNDER': return '⛈️';
    case 'SNOW': return '❄️';
    case 'WIND': return '💨';
    default: return '🌡️';
  }
}
